import { useEffect, useState, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from 'bulma-toast';
import ChatBox from '../components/channel/ChatBox';
import ChannelConnection from '../js/connection/ChannelConnection';
import PainterroUtils from '../components/channel/PainterroUtils';
// import useUser from '../js/utils/useUser';
import useHoloXRPageTitle from '../js/utils/useHoloXRPageTitle';
import liveswitch from 'fm.liveswitch';
import ChannelToolbar from '../components/channel/ChannelToolbar';
import ChannelValidationPage from '../components/channel/ChannelValidationPage';
import SFULayoutOverlay from '../components/channel/SFULayoutOverlay';
import ChannelBanner from '../components/channel/ChannelBanner';
import { Box, Button, Columns } from 'react-bulma-components';
import ConnectionData from '../js/connection/ConnectionData';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import ThumbnailPanel from "../components/channel/ThumbnailPanel";
import RootOverflowHandler from '../components/channel/RootOverflowHandler';
import ChannelSettingsModal from '../components/channel/ChannelSettingsModal';
import LeaveChannelModal from '../components/channel/LeaveChannelModal';

export default function Channel() {
    useHoloXRPageTitle('Channel');

    const connection = useRef(new ChannelConnection());
    
    const channelId = useRef(useParams().id);
    const [,setSearchParams] = useSearchParams();
    //const { user } = {};//useUser();
    const [localMedia, setLocalMedia] = useState<liveswitch.LocalMedia>();
    const [chatOpened, setChatOpened] = useState<boolean>(window.innerWidth >= 768);
    const [thumbnailsOpened, setThumbnailsOpened] = useState<boolean>(false);
    const [isMobile, setMobile] = useState<boolean>(window.innerWidth < 768);
    const [currentTime, setCurrentTime] = useState<Date>(new Date());
    const [sfuConnections, setSfuConnections] = useState<ConnectionData[]>([]);
    const [localMediaState, setLocalMediaState] = useState<number>(0);
    const [isSettingsModalOpen, setSettingsModalOpen] = useState(false);
    const [controlRequest, setControlRequest] = useState<{ fromId: string, fromAlias: string } | null>(null);
    const [screenshareState, setScreenshareState] = useState<number>(0);
    const navigate = useNavigate();

    useEffect(() => {
        function handleResize() {
            setMobile(window.innerWidth < 768);
        }

        window.addEventListener('resize', handleResize);
        handleResize();

        // Cleanup the event listener on component unmount
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        const onLocalMediaUpdate = () => {
            setLocalMediaState(prev => prev + 1);
        }

        const onControlRequest = (request: { fromId: string, fromAlias: string, data: any }) => {
            setControlRequest({ fromId: request.fromId, fromAlias: request.data.requestingUserAlias });
        };

        const onScreenshareUpdate = () => {
            setScreenshareState(prev => prev + 1);
        }

        const conn = connection.current;
        conn.events.addListener("onlocalmediaupdate", onLocalMediaUpdate);
        conn.events.addListener("onrequestcontrol", onControlRequest);
        conn.events.addListener("onscreenshareupdate", onScreenshareUpdate);

        return () => {
            conn.events.removeListener("onlocalmediaupdate", onLocalMediaUpdate);
            conn.events.removeListener("onrequestcontrol", onControlRequest);
            conn.events.removeListener("onscreenshareupdate", onScreenshareUpdate);
        }
    }, []);

    useEffect(() => {
        if (localMedia) {
            async function openConnection() {
                setSearchParams({});
                connection.current.openConnection(localMedia!);
            }
            openConnection();
        }
    }, [setSearchParams, localMedia]);

    // Cleanup: Leave channel when component unmounts or browser closes
    useEffect(() => {
        const handleBeforeUnload = () => {
            // Cleanup connection when browser tab/window is closed
            if (connection.current) {
                connection.current.leaveChannel();
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            // Cleanup connection when component unmounts (user navigates away)
            window.removeEventListener('beforeunload', handleBeforeUnload);
            if (connection.current) {
                connection.current.leaveChannel();
            }
        };
    }, []);

    if (!localMedia) {
        return <ChannelValidationPage connection={connection.current} onConfirm={(lm) => setLocalMedia(lm)} />;
    }

    return (
        <>
            <RootOverflowHandler connection={connection.current} />
            <div style={{ height: "100vh", display: "flex", flexDirection: "column" }}>
                <ChannelBanner connection={connection.current} />

                <Columns style={{ margin: "0 20px 20px 20px", flex: 1 }}>
                    <Columns.Column style={{ display: "flex", flexDirection: "column", height: "100%", padding: 0 }}>
                        <Box id="video" style={{ 
                            flex: 1, 
                            borderRadius: 14, 
                            padding: 0, 
                            overflow: "hidden",
                            position: "relative",
                            minHeight: "300px",
                            backgroundColor: "#000",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                        }} />
                        <ChannelToolbar 
                            connection={connection.current} 
                            isChatOpened={chatOpened} 
                            isMobileView={isMobile} 
                            toggleChatOpened={() => {
                                setChatOpened(!chatOpened);
                                if (!chatOpened) {
                                    setThumbnailsOpened(false); // Close thumbnails when opening chat
                                }
                            }}
                            isThumbnailsOpened={thumbnailsOpened}
                            toggleThumbnailsOpened={() => {
                                setThumbnailsOpened(!thumbnailsOpened);
                                if (!thumbnailsOpened) {
                                    setChatOpened(false); // Close chat when opening thumbnails
                                }
                            }}
                        />
                    </Columns.Column>

                    {chatOpened && !isMobile && (
                        <Columns.Column size={4} style={{ padding: "0 0 0 20px" }}>
                            <ChatBox connection={connection.current} />
                        </Columns.Column>
                    )}

                    {thumbnailsOpened && !isMobile && (
                        <Columns.Column size={4} style={{ padding: "0 0 0 20px" }}>
                            <ThumbnailPanel 
                                connections={Object.values(connection.current.sfuConnection.connections)}
                                isMobileView={isMobile}
                                onClose={() => setThumbnailsOpened(false)}
                            />
                        </Columns.Column>
                    )}

                    {chatOpened && isMobile && (
                        <div style={{ 
                            position: "absolute", 
                            top: 0, 
                            left: 0, 
                            width: "100vw", 
                            height: "100vh", 
                            backgroundColor: "rgba(0, 0, 0, 0.5)",
                            backdropFilter: "blur(4px)",
                            zIndex: 22
                        }}>
                            <div style={{ 
                                position: "absolute", 
                                top: 0, 
                                right: 0, 
                                height: "100%", 
                                width: "100%",
                                backgroundColor: "#ffffff",
                                borderRadius: "12px 0 0 12px",
                                padding: "1rem"
                            }}>
                                <Button 
                                    rounded 
                                    onClick={() => setChatOpened(false)} 
                                    style={{ 
                                        position: "absolute", 
                                        top: 30, 
                                        right: 30,
                                        width: "2.5rem",
                                        height: "2.5rem",
                                        backgroundColor: "#ffffff",
                                        border: "1px solid rgba(0, 0, 0, 0.1)",
                                        color: "#000000",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        padding: 0
                                    }}>
                                    <FontAwesomeIcon icon={faTimes} />
                                </Button>
                                <ChatBox connection={connection.current} />
                            </div>
                        </div>
                    )}

                    {thumbnailsOpened && isMobile && (
                        <div style={{ 
                            position: "absolute", 
                            top: 0, 
                            left: 0, 
                            width: "100vw", 
                            height: "100vh", 
                            backgroundColor: "rgba(0, 0, 0, 0.5)",
                            backdropFilter: "blur(4px)"
                        }}>
                            <div style={{ 
                                position: "absolute", 
                                top: 0, 
                                right: 0, 
                                height: "100%", 
                                width: "100%",
                                backgroundColor: "#ffffff",
                                borderRadius: "12px 0 0 12px",
                                padding: "1rem"
                            }}>
                                <Button 
                                    rounded 
                                    onClick={() => setThumbnailsOpened(false)} 
                                    style={{ 
                                        position: "absolute", 
                                        top: 10, 
                                        right: 10,
                                        width: "2.5rem",
                                        height: "2.5rem",
                                        backgroundColor: "#ffffff",
                                        border: "1px solid rgba(0, 0, 0, 0.1)",
                                        color: "#000000",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        padding: 0
                                    }}>
                                    <FontAwesomeIcon icon={faTimes} />
                                </Button>
                                <ThumbnailPanel 
                                    connections={Object.values(connection.current.sfuConnection.connections)}
                                    isMobileView={isMobile}
                                    onClose={() => setThumbnailsOpened(false)}
                                />
                            </div>
                        </div>
                    )}
                </Columns>
                <SFULayoutOverlay connection={connection.current} />

                {controlRequest && (
                    <div className="notification is-info" style={{ position: 'absolute', top: '20px', left: '50%', transform: 'translateX(-50%)', zIndex: 9999 }}>
                        <button className="delete" onClick={() => setControlRequest(null)}></button>
                        {controlRequest.fromAlias} would like to control your screen.
                        <div className="buttons" style={{ marginTop: '10px' }}>
                            <button className="button is-primary">Accept</button>
                            <button className="button" onClick={() => setControlRequest(null)}>Deny</button>
                        </div>
                    </div>
                )}

                {localMedia && (
                    <ChannelSettingsModal
                        localMedia={localMedia}
                        show={isSettingsModalOpen}
                        onClose={() => setSettingsModalOpen(false)}
                    />
                )}

                <LeaveChannelModal
                    title="Leave the meeting?"
                    onSuccess={() => {
                        connection.current.leaveChannel();
                        navigate("/");
                    }}
                    onCancel={() => {
                        const confirmLeave = document.getElementById("confirmLeave");
                        if (confirmLeave) {
                            confirmLeave.classList.remove("is-active");
                        }
                    }}
                />
            </div>
        </>
    );
}
